import React, { createContext, useContext, useEffect, useState } from 'react';
import { THEME_CONFIG, THEME_MODES, RESPONSIVE_CONFIG, SCALE_CONFIG } from '../config/themes';

const ThemeContext = createContext(undefined);

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to light
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('theme-mode');
    if (stored && Object.values(THEME_MODES).includes(stored)) {
      return stored;
    }
    return THEME_MODES.LIGHT; // Default to light, no system detection
  });

  // Device detection for responsive design (using scaled breakpoints)
  const [deviceType, setDeviceType] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    
    const width = window.innerWidth;
    if (width <= RESPONSIVE_CONFIG.devices.mobile.maxWidth) return 'mobile';
    if (width <= RESPONSIVE_CONFIG.devices.tablet.maxWidth) return 'tablet';
    return 'desktop';
  });

  // Listen to window resize for responsive changes (using scaled breakpoints)
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= RESPONSIVE_CONFIG.devices.mobile.maxWidth) {
        setDeviceType('mobile');
      } else if (width <= RESPONSIVE_CONFIG.devices.tablet.maxWidth) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Apply CSS custom properties for both theme and responsive values
  useEffect(() => {
    const root = document.documentElement;
    const themeConfig = THEME_CONFIG[theme];
    const responsiveSpacing = RESPONSIVE_CONFIG.spacing[deviceType === 'mobile' ? 'mobile' : 'desktop'];
    const responsiveTypography = RESPONSIVE_CONFIG.typography[deviceType === 'mobile' ? 'mobile' : 'desktop'];
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(theme);
    
    // Apply global scale factor as CSS custom property
    root.style.setProperty('--global-scale', SCALE_CONFIG.GLOBAL_SCALE);
    
    // Apply theme CSS custom properties
    Object.entries(themeConfig).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    // Apply responsive spacing custom properties (already scaled)
    Object.entries(responsiveSpacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-${key}`, value);
    });

    // Apply responsive typography custom properties (already scaled)
    Object.entries(responsiveTypography).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    // Apply device type class for responsive styles
    root.classList.remove('device-mobile', 'device-tablet', 'device-desktop');
    root.classList.add(`device-${deviceType}`);
    
  }, [theme, deviceType]);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme-mode', theme);
  }, [theme]);

  // Simple toggle between light and dark (no system option)
  const toggleTheme = () => {
    setTheme(current => current === THEME_MODES.LIGHT ? THEME_MODES.DARK : THEME_MODES.LIGHT);
  };

  // Set specific theme mode
  const setThemeMode = (mode) => {
    if (Object.values(THEME_MODES).includes(mode)) {
      setTheme(mode);
    }
  };

  // Get responsive values based on current device (uses scaled values)
  const getResponsiveValue = (values) => {
    if (typeof values === 'string') return values;
    
    if (deviceType === 'mobile') return values.mobile || values.default;
    if (deviceType === 'tablet') return values.tablet || values.desktop || values.default;
    return values.desktop || values.default;
  };

  // Get component size configuration (returns scaled values)
  const getComponentSize = (component, size = 'md') => {
    const deviceKey = deviceType === 'mobile' ? 'mobile' : 'desktop';
    return RESPONSIVE_CONFIG.components[component]?.[deviceKey]?.[size] || {};
  };

  // Utility to manually scale a value (useful for one-off cases)
  const scaleValue = (value) => {
    return SCALE_CONFIG.scale(value);
  };

  const value = {
    // Theme properties
    theme,
    setTheme: setThemeMode,
    toggleTheme,
    isDark: theme === THEME_MODES.DARK,
    isLight: theme === THEME_MODES.LIGHT,
    THEME_MODES,
    
    // Responsive properties (using scaled values)
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet', 
    isDesktop: deviceType === 'desktop',
    getResponsiveValue,
    getComponentSize,
    
    // Scale utilities
    scaleValue,
    globalScale: SCALE_CONFIG.GLOBAL_SCALE,
    
    // Configuration access
    RESPONSIVE_CONFIG,
    SCALE_CONFIG
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};